import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Settings, User, Shield, UserX, Camera, Eye, EyeOff, AlertTriangle, Trash2, Lock, Mail, Users } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  useProfileData,
  useUpdateProfile,
  useChangePassword,
  useDeleteAccount,
  useDeactivateAccount
} from '@/hooks/api-hooks';
import { getInitials, getAvatarColor } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel
} from '@/components/ui/alert-dialog';

export function ProfilePage() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { data: profileData, isLoading } = useProfileData();
  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const deleteAccountMutation = useDeleteAccount();
  const deactivateAccountMutation = useDeactivateAccount();

  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || ''
  });
  const [initialProfile, setInitialProfile] = useState(profileForm);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [initialPhoto, setInitialPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previousPreviewRef = useRef<string | null>(null);
  const initialPhotoRef = useRef<string | null>(null);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivateForm, setDeactivateForm] = useState({ reason: '', feedback: '' });
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteForm, setDeleteForm] = useState({ password: '', reason: '', feedback: '', confirmText: '' });
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof ApiError) {
      try {
        const parsed = JSON.parse(error.message);
        if (parsed && typeof parsed === 'object') {
          if ('error' in parsed && typeof parsed.error === 'string') {
            return parsed.error;
          }
          if ('message' in parsed && typeof parsed.message === 'string') {
            return parsed.message;
          }
        }
      } catch {
        if (error.message) {
          return error.message;
        }
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallback;
  };

  useEffect(() => {
    if (!profileData) {
      return;
    }

    const syncedProfile = {
      firstName: profileData.firstName || '',
      lastName: profileData.lastName || '',
      email: profileData.email || '',
    };

    const nextPhotoUrl = profileData.photoUrl ?? null;

    const hasUnsavedChanges =
      profileForm.firstName.trim() !== initialProfile.firstName.trim() ||
      profileForm.lastName.trim() !== initialProfile.lastName.trim() ||
      (photoPreview ?? null) !== (initialPhoto ?? null) ||
      !!photoFile;

    if (hasUnsavedChanges) {
      // Preserve user edits until they are saved or reset.
      return;
    }

    if (
      profileForm.firstName !== syncedProfile.firstName ||
      profileForm.lastName !== syncedProfile.lastName ||
      profileForm.email !== syncedProfile.email
    ) {
      setProfileForm(syncedProfile);
    }

    if (
      initialProfile.firstName !== syncedProfile.firstName ||
      initialProfile.lastName !== syncedProfile.lastName ||
      initialProfile.email !== syncedProfile.email
    ) {
      setInitialProfile(syncedProfile);
    }

    if (initialPhoto !== nextPhotoUrl) {
      setInitialPhoto(nextPhotoUrl);
      initialPhotoRef.current = nextPhotoUrl;
    }

    if (photoPreview !== nextPhotoUrl) {
      setPhotoPreview(nextPhotoUrl);
      previousPreviewRef.current = nextPhotoUrl;
    }
  }, [profileData, photoFile, photoPreview, profileForm, initialProfile, initialPhoto]);

  useEffect(() => {
    const previous = previousPreviewRef.current;
    if (previous && previous.startsWith('blob:') && previous !== photoPreview && previous !== initialPhotoRef.current) {
      URL.revokeObjectURL(previous);
    }
    previousPreviewRef.current = photoPreview;
  }, [photoPreview]);

  useEffect(() => {
    const previous = initialPhotoRef.current;
    if (previous && previous.startsWith('blob:') && previous !== photoPreview && previous !== previousPreviewRef.current) {
      URL.revokeObjectURL(previous);
    }
    initialPhotoRef.current = initialPhoto;
  }, [initialPhoto, photoPreview]);

  useEffect(() => {
    return () => {
      const preview = previousPreviewRef.current;
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }

      const initial = initialPhotoRef.current;
      if (initial && initial.startsWith('blob:')) {
        URL.revokeObjectURL(initial);
      }
    };
  }, []);

  const handleProfileSave = async (overrides?: {
    photoFile?: File | null;
    photoRemoved?: boolean;
    toastMessage?: { title: string; description?: string };
  }): Promise<boolean> => {
    const nextProfile = {
      firstName: profileForm.firstName.trim(),
      lastName: profileForm.lastName.trim(),
      email: profileForm.email
    };

    const photoFileToUpload = overrides?.photoFile === undefined
      ? photoFile ?? undefined
      : overrides.photoFile ?? undefined;

    const removePhoto = overrides?.photoRemoved ?? (initialPhoto !== null && photoPreview === null);

    try {
      const response = await updateProfileMutation.mutateAsync({
        ...nextProfile,
        photoFile: photoFileToUpload,
        photoRemoved: removePhoto,
      });

      const savedProfile = response.data;
      const savedPhoto = savedProfile?.photoUrl ?? null;

      if (photoPreview && photoPreview.startsWith('blob:') && photoPreview !== savedPhoto) {
        URL.revokeObjectURL(photoPreview);
      }

      setInitialProfile({
        firstName: savedProfile.firstName,
        lastName: savedProfile.lastName,
        email: savedProfile.email,
      });

      setProfileForm({
        firstName: savedProfile.firstName,
        lastName: savedProfile.lastName,
        email: savedProfile.email,
      });

      setInitialPhoto(savedPhoto);
      initialPhotoRef.current = savedPhoto;
      setPhotoFile(null);
      setPhotoPreview(savedPhoto);
      previousPreviewRef.current = savedPhoto;
      toast(
        overrides?.toastMessage ?? {
          title: 'Profile updated',
          description: 'Your changes have been saved.',
        }
      );

      return true;
    } catch (error) {
      console.error('Failed to update profile', error);
      const message = getErrorMessage(error, 'Please try again.');
      toast({
        title: 'Profile update failed',
        description: message,
        variant: 'destructive',
      });

      return false;
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return;
    }

    try {
      setPasswordError(null);
      await changePasswordMutation.mutateAsync({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      toast({
        title: 'Password changed',
        description: 'Your password was updated successfully.',
      });
    } catch (error) {
      console.error('Failed to change password', error);
      const message = getErrorMessage(error, 'Please confirm your current password and try again.');
      setPasswordError(message);
      toast({
        title: 'Password change failed',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const openPhotoPicker = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const currentPreview = photoPreview;
    if (currentPreview && currentPreview.startsWith('blob:') && currentPreview !== initialPhotoRef.current) {
      URL.revokeObjectURL(currentPreview);
    }

    const previewUrl = URL.createObjectURL(file);
    setPhotoFile(file);
    setPhotoPreview(previewUrl);
    previousPreviewRef.current = previewUrl;
    event.target.value = '';

    const success = await handleProfileSave({
      photoFile: file,
      toastMessage: {
        title: 'Profile photo updated',
        description: 'Your avatar has been saved.',
      }
    });

    if (!success) {
      if (previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      setPhotoPreview(initialPhoto);
      previousPreviewRef.current = initialPhoto;
      setPhotoFile(null);
    }
  };

  const handlePhotoRemove = () => {
    if (photoPreview && photoPreview.startsWith('blob:') && photoPreview !== initialPhotoRef.current) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview(null);
    previousPreviewRef.current = null;
  };

  const handleDeactivateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const reason = deactivateForm.reason.trim();
    const feedback = deactivateForm.feedback.trim();

    if (!reason) {
      setDeactivateError('Please provide a reason for deactivating your account.');
      return;
    }

    setDeactivateError(null);

    try {
      await deactivateAccountMutation.mutateAsync({
        reason,
        feedback: feedback ? feedback : undefined,
      });

      toast({
        title: 'Account deactivated',
        description: 'You can sign back in to reactivate your account.',
      });

      setDeactivateDialogOpen(false);
      setDeactivateForm({ reason: '', feedback: '' });
      logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Failed to deactivate account', error);
      const message = getErrorMessage(error, 'Failed to deactivate account.');
      setDeactivateError(message);
    }
  };

  const handleDeleteSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const reason = deleteForm.reason.trim();
    const feedback = deleteForm.feedback.trim();
    const password = deleteForm.password.trim();
    const confirmText = deleteForm.confirmText.trim();

    if (!reason) {
      setDeleteError('Please provide a reason for deleting your account.');
      return;
    }

    if (confirmText.toUpperCase() !== 'DELETE MY ACCOUNT') {
      setDeleteError('Confirmation text must match DELETE MY ACCOUNT.');
      return;
    }

    setDeleteError(null);

    try {
      await deleteAccountMutation.mutateAsync({
        password: password ? password : undefined,
        reason,
        feedback: feedback ? feedback : undefined,
        confirmText: confirmText,
      });

      toast({
        title: 'Account deleted',
        description: 'Your data has been scheduled for removal.',
      });

      setDeleteDialogOpen(false);
      setDeleteForm({ password: '', reason: '', feedback: '', confirmText: '' });
      logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to delete account', error);
      const message = getErrorMessage(error, 'Failed to delete account.');
      setDeleteError(message);
    }
  };

  const resetDeactivateDialog = () => {
    setDeactivateForm({ reason: '', feedback: '' });
    setDeactivateError(null);
    deactivateAccountMutation.reset();
  };

  const resetDeleteDialog = () => {
    setDeleteForm({ password: '', reason: '', feedback: '', confirmText: '' });
    setDeleteError(null);
    deleteAccountMutation.reset();
  };

  const canDeactivateSubmit = deactivateForm.reason.trim().length > 0 && !deactivateAccountMutation.isPending;
  const deleteConfirmValid = deleteForm.confirmText.trim().toUpperCase() === 'DELETE MY ACCOUNT';
  const canDeleteSubmit =
    deleteForm.reason.trim().length > 0 &&
    deleteConfirmValid &&
    !deleteAccountMutation.isPending;

  const hasProfileChanges =
    profileForm.firstName.trim() !== initialProfile.firstName.trim() ||
    profileForm.lastName.trim() !== initialProfile.lastName.trim() ||
    (photoPreview ?? null) !== initialPhoto;

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const passwordStrength = getPasswordStrength(passwordForm.newPassword);

  const getStrengthText = (strength: number) => {
    if (strength < 25) return 'Weak';
    if (strength < 50) return 'Fair';
    if (strength < 75) return 'Good';
    return 'Strong';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Profile Settings</h1>
              <p className="text-muted-foreground">Manage your account settings and preferences</p>
            </div>
        <div className="flex items-center space-x-2">
          <Badge variant="secondary" className="flex items-center space-x-1">
            <Users className="h-3 w-3" />
            <span>{user?.role}</span>
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="profile" className="flex items-center space-x-2">
            <User className="h-4 w-4" />
            <span>Profile</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>Security</span>
          </TabsTrigger>
          <TabsTrigger value="account" className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Account</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Personal Information</span>
              </CardTitle>
              <CardDescription>
                Update your personal details and profile information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Photo Section */}
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src={photoPreview || profileData?.photoUrl || undefined} />
                    <AvatarFallback className={`text-lg font-semibold ${getAvatarColor(user?.firstName || '', user?.lastName || '')}`}>
                      {getInitials(user?.firstName || '', user?.lastName || '')}
                    </AvatarFallback>
                  </Avatar>
                  <Button
                    size="sm"
                    variant="outline"
                    className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full p-0"
                    onClick={openPhotoPicker}
                    type="button"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium">Profile Photo</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload a photo to personalize your profile
                  </p>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={openPhotoPicker} type="button">
                      Upload Photo
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handlePhotoRemove}
                      type="button"
                      disabled={!photoPreview && !profileData?.photoUrl}
                    >
                      Remove
                    </Button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoSelected}
                  />
                </div>
              </div>

              <Separator />

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={profileForm.firstName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, firstName: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={profileForm.lastName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, lastName: e.target.value }))}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={profileForm.email}
                      className="pl-10"
                      disabled
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Contact support to change your email address
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (photoPreview && photoPreview.startsWith('blob:')) {
                      URL.revokeObjectURL(photoPreview);
                    }
                    setProfileForm({ ...initialProfile });
                    setPhotoFile(null);
                    setPhotoPreview(initialPhoto);
                    previousPreviewRef.current = initialPhoto;
                  }}
                  disabled={updateProfileMutation.isPending || !hasProfileChanges}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    void handleProfileSave();
                  }}
                  disabled={
                    !profileForm.firstName.trim() ||
                    !profileForm.lastName.trim() ||
                    updateProfileMutation.isPending ||
                    !hasProfileChanges
                  }
                >
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          {/* Password Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="h-5 w-5" />
                <span>Password & Authentication</span>
              </CardTitle>
              <CardDescription>
                Manage your password and two-factor authentication settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => {
                        setPasswordError(null);
                        setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }));
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => {
                        setPasswordError(null);
                        setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }));
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {passwordForm.newPassword && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Password strength:</span>
                        <span className={`font-medium ${passwordStrength < 50 ? 'text-red-600' : passwordStrength < 75 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {getStrengthText(passwordStrength)}
                        </span>
                      </div>
                      <Progress value={passwordStrength} className="h-2" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => {
                        setPasswordError(null);
                        setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }));
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                    <p className="text-sm text-red-600">Passwords do not match</p>
                  )}
                </div>

                <Button
                  onClick={handlePasswordChange}
                  disabled={
                    !passwordForm.currentPassword ||
                    !passwordForm.newPassword ||
                    !passwordForm.confirmPassword ||
                    passwordForm.newPassword !== passwordForm.confirmPassword ||
                    changePasswordMutation.isPending
                  }
                >
                  {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
                </Button>
                {passwordError && (
                  <p className="text-sm text-red-600">{passwordError}</p>
                )}
              </div>
            </CardContent>
          </Card>

        </TabsContent>

        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                <span>Danger Zone</span>
              </CardTitle>
              <CardDescription>
                Permanent actions that cannot be undone
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  These actions are permanent and cannot be reversed. Please proceed with caution.
                </AlertDescription>
              </Alert>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-medium">Deactivate Account</h4>
                  <p className="text-sm text-muted-foreground">
                    Temporarily disable your account. You can reactivate it later.
                  </p>
                </div>
                <AlertDialog
                  open={deactivateDialogOpen}
                  onOpenChange={(open) => {
                    setDeactivateDialogOpen(open);
                    if (!open) {
                      resetDeactivateDialog();
                    }
                  }}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      type="button"
                      className="text-orange-600 border-orange-600 hover:bg-orange-50"
                      disabled={deactivateAccountMutation.isPending}
                    >
                      <UserX className="h-4 w-4 mr-2" />
                      Deactivate
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <form onSubmit={handleDeactivateSubmit} className="space-y-4">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Deactivate account</AlertDialogTitle>
                        <AlertDialogDescription>
                          Your account will be disabled until you sign back in.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="deactivate-reason">Reason</Label>
                          <Textarea
                            id="deactivate-reason"
                            value={deactivateForm.reason}
                            onChange={(event) =>
                              setDeactivateForm(prev => ({ ...prev, reason: event.target.value }))
                            }
                            required
                            placeholder="Let us know why you want to deactivate."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="deactivate-feedback">Additional feedback (optional)</Label>
                          <Textarea
                            id="deactivate-feedback"
                            value={deactivateForm.feedback}
                            onChange={(event) =>
                              setDeactivateForm(prev => ({ ...prev, feedback: event.target.value }))
                            }
                            placeholder="Anything we could improve?"
                          />
                        </div>
                        {deactivateError && (
                          <p className="text-sm text-red-600">{deactivateError}</p>
                        )}
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel
                          type="button"
                          disabled={deactivateAccountMutation.isPending}
                        >
                          Cancel
                        </AlertDialogCancel>
                        <Button
                          type="submit"
                          variant="outline"
                          className="text-orange-600 border-orange-600 hover:bg-orange-50"
                          disabled={!canDeactivateSubmit}
                        >
                          {deactivateAccountMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                        </Button>
                      </AlertDialogFooter>
                    </form>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
                <div>
                  <h4 className="font-medium text-red-800">Delete Account</h4>
                  <p className="text-sm text-red-600">
                    Permanently delete your account and all associated data. This cannot be undone.
                  </p>
                </div>
                <AlertDialog
                  open={deleteDialogOpen}
                  onOpenChange={(open) => {
                    setDeleteDialogOpen(open);
                    if (!open) {
                      resetDeleteDialog();
                    }
                  }}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      type="button"
                      disabled={deleteAccountMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <form onSubmit={handleDeleteSubmit} className="space-y-4">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete account</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently remove your account and associated data.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="delete-reason">Reason</Label>
                          <Textarea
                            id="delete-reason"
                            value={deleteForm.reason}
                            onChange={(event) =>
                              setDeleteForm(prev => ({ ...prev, reason: event.target.value }))
                            }
                            required
                            placeholder="Tell us why you're leaving."
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="delete-feedback">Additional feedback (optional)</Label>
                          <Textarea
                            id="delete-feedback"
                            value={deleteForm.feedback}
                            onChange={(event) =>
                              setDeleteForm(prev => ({ ...prev, feedback: event.target.value }))
                            }
                            placeholder="Any other thoughts you'd like to share?"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="delete-password">Password (if set)</Label>
                          <Input
                            id="delete-password"
                            type="password"
                            value={deleteForm.password}
                            onChange={(event) =>
                              setDeleteForm(prev => ({ ...prev, password: event.target.value }))
                            }
                            placeholder="Enter your password"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="delete-confirm">Confirmation</Label>
                          <Input
                            id="delete-confirm"
                            value={deleteForm.confirmText}
                            onChange={(event) =>
                              setDeleteForm(prev => ({ ...prev, confirmText: event.target.value }))
                            }
                            placeholder="Type DELETE MY ACCOUNT"
                          />
                          <p className="text-xs text-muted-foreground">
                            Type DELETE MY ACCOUNT to confirm.
                          </p>
                        </div>
                        {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel
                          type="button"
                          disabled={deleteAccountMutation.isPending}
                        >
                          Cancel
                        </AlertDialogCancel>
                        <Button
                          type="submit"
                          variant="destructive"
                          disabled={!canDeleteSubmit}
                        >
                          {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
                        </Button>
                      </AlertDialogFooter>
                    </form>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </div>
      </div>
    </div>
  );
}
